import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../auth/useAuth';
import { loadConfig } from '../config';
import { createApiClient } from '../api/client';
import { createDetectApi, type DetectApi } from '../api/detect';
import { ApiError, describeApiError } from '../api/errors';

const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 240_000;
const POST_TIMEOUT_MS = 30_000;
const GET_TIMEOUT_MS = 15_000;
/** Consecutive failed polls tolerated before the job is abandoned. */
const MAX_POLL_ERRORS = 5;

export type DetectState = {
  status: 'idle' | 'submitting' | 'queued' | 'running' | 'succeeded' | 'failed' | 'error';
  result?: Record<string, unknown>;
  error?: string;
  jobId?: string;
  /** Seconds the worker has been on this job — running jobs only. */
  elapsedS?: number;
};

/**
 * Cancellation signal plus a hard timeout. `AbortSignal.any` is missing in
 * jsdom (and older Safari), so bridge the two sources by hand when absent.
 */
function withTimeout(signal: AbortSignal, ms: number): AbortSignal {
  const timeout = AbortSignal.timeout(ms);
  const combine = (AbortSignal as unknown as {
    any?: (signals: AbortSignal[]) => AbortSignal;
  }).any;
  if (combine) return combine.call(AbortSignal, [signal, timeout]);

  const merged = new AbortController();
  for (const source of [signal, timeout]) {
    if (source.aborted) {
      merged.abort(source.reason);
      break;
    }
    source.addEventListener('abort', () => merged.abort(source.reason), { once: true });
  }
  return merged.signal;
}

function describeSubmitError(err: unknown): string {
  const name = (err as Error | undefined)?.name;
  if (name === 'TimeoutError') return 'The scan service did not respond within 30 seconds. Try again.';
  if (err instanceof ApiError && err.status === 401) return 'Your session expired — sign in again.';
  return describeApiError(err);
}

function describePollError(err: unknown): string {
  if (err instanceof ApiError) return `HTTP ${err.status}`;
  return (err as Error | undefined)?.name ?? 'network error';
}

export function useDetect() {
  const { getValidAccessToken } = useAuth();
  const [state, setState] = useState<DetectState>({ status: 'idle' });
  const [isScanning, setIsScanning] = useState(false);

  // Every scan owns a generation. reset(), a new scan() and unmount all bump
  // it, so a response that lands late belongs to a dead generation and must
  // never reach setState.
  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const cancelInFlight = useCallback(() => {
    generationRef.current += 1;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancelInFlight();
    };
  }, [cancelInFlight]);

  const scan = useCallback(async (url: string) => {
    cancelInFlight();
    const generation = generationRef.current;
    const controller = new AbortController();
    abortRef.current = controller;

    const isCurrent = () => mountedRef.current && generationRef.current === generation;
    const commit = (next: DetectState, scanning: boolean) => {
      if (!isCurrent()) return;
      setState(next);
      setIsScanning(scanning);
    };
    const fail = (error: string, jobId?: string) => commit({ status: 'error', error, jobId }, false);

    commit({ status: 'submitting' }, true);

    let api: DetectApi;
    let jobId: string;
    try {
      const config = await loadConfig();
      if (!isCurrent()) return;
      api = createDetectApi(createApiClient(getValidAccessToken), config.postUrl, config.getUrl);

      const submitted = await api.postIdentify(url, withTimeout(controller.signal, POST_TIMEOUT_MS));
      if (!isCurrent()) return;

      // 200 cache hit — the result is already here, there is no job to poll.
      if (submitted.status === 'succeeded' && submitted.result) {
        commit({ status: 'succeeded', result: submitted.result }, false);
        return;
      }
      if (!submitted.job_id) {
        fail('The scan service accepted the request without returning a job id.');
        return;
      }
      jobId = submitted.job_id;
      commit({ status: submitted.status === 'running' ? 'running' : 'queued', jobId }, true);
    } catch (err) {
      if (!isCurrent()) return;
      fail(describeSubmitError(err));
      return;
    }

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    let consecutiveErrors = 0;

    const schedule = () => {
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    const poll = async () => {
      // Only clear the handle we own: a stale generation's callback must not
      // wipe the timer a newer scan just scheduled.
      if (!isCurrent()) return;
      timerRef.current = null;
      if (Date.now() > deadline) {
        fail('The scan timed out. Try again.', jobId);
        return;
      }

      try {
        // Per-poll timeout: a status read that never settles would otherwise
        // stall the loop forever — the deadline below is only read at entry.
        const job = await api.getJob(jobId, withTimeout(controller.signal, GET_TIMEOUT_MS));
        if (!isCurrent()) return;
        consecutiveErrors = 0;

        if (job.status === 'succeeded') {
          commit({ status: 'succeeded', result: job.result, jobId }, false);
          return;
        }
        if (job.status === 'failed') {
          commit(
            { status: 'failed', error: job.message ?? job.error ?? 'Detection failed', jobId },
            false,
          );
          return;
        }

        commit(
          {
            status: job.status === 'running' ? 'running' : 'queued',
            jobId,
            elapsedS: job.elapsed_s,
          },
          true,
        );
        schedule();
      } catch (err) {
        if (!isCurrent()) return;
        // The job row is gone (expired or never existed) — it is never coming
        // back, so stop instead of polling to the deadline.
        if (err instanceof ApiError && err.status === 404) {
          fail('This scan job expired. Run the scan again.', jobId);
          return;
        }
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_POLL_ERRORS) {
          fail(`Lost contact with the scan service (${describePollError(err)}). Try again.`, jobId);
          return;
        }
        schedule();
      }
    };

    schedule();
  }, [cancelInFlight, getValidAccessToken]);

  const reset = useCallback(() => {
    cancelInFlight();
    setState({ status: 'idle' });
    setIsScanning(false);
  }, [cancelInFlight]);

  return { state, isScanning, scan, reset };
}
